/**
 * SO-line sales-override → 價格覆寫報表 review row (S9.2).
 *
 * Plan §S9.2: "sales override → mandatory reason + 價格覆寫報表 review row".
 * The mandatory reason is enforced by PriceOverrideModal/ReasonRequiredModal
 * and the MSW handler (OVERRIDE_REASON_REQUIRED); this module lands the
 * PERSISTED override (called on successful create/modify save, never on a
 * draft edit the user may abandon) in the same PRICE_OVERRIDE review lens the
 * S9.1 special-price saves feed —「報表送出給高權限看過」.
 *
 * S1.2 (2026-08-29): the store union carries 'so_line' first-class and the
 * row lands through the store's own `appendAuditRow` action — the recorded
 * widen-the-union follow-up is done, so no setState from src/ and no cast.
 *
 * RULING-7 (2026-08-29): the S3.3 detach row obeys the same save-time
 * doctrine as the override rows — the create page QUEUES the detach on the
 * draft line (`DraftSoLine.detachedOverride`, last wins) and flushes it here
 * only after `createSalesOrder` succeeds, stamped with the real soNumber;
 * the modify page writes immediately against its persisted SO. An abandoned
 * draft lands NO row.
 */

import { getUserProfile } from '@/services/api/axios.config';
import type { User } from '@/models/user';
import { usePricingStore } from '@/stores/pricingStore';
import type { PriceOverrideAuditRow } from '@/stores/pricingStore';

export interface SoLineOverrideAuditInput {
  soNumber: string;
  sku: string;
  currency: string;
  /** System-resolved price at save time; null = unresolvable line. */
  resolvedUnitPrice: number | null;
  /** Transacted (manually overridden) price. */
  unitPrice: number;
  /** The logged mandatory business reason. */
  reason: string;
}

let auditSeq = 0;

/** "resolved 140.00 → manual 175.00 AUD" old→new one-liner (store style). */
export function summarizeSoLineOverride(input: SoLineOverrideAuditInput): string {
  const resolved =
    input.resolvedUnitPrice !== null ? input.resolvedUnitPrice.toFixed(2) : 'unresolved';
  return `${input.soNumber} · ${input.sku}: resolved ${resolved} → manual ${input.unitPrice.toFixed(2)} ${input.currency}`;
}

/** Append one persisted SO-line override to the PRICE_OVERRIDE review rows. */
export function appendSoLineOverrideAudit(input: SoLineOverrideAuditInput): void {
  const actorName = (getUserProfile() as User | null)?.name ?? 'unknown';
  const row: PriceOverrideAuditRow = {
    id: `so-override-${Date.now()}-${(auditSeq += 1)}`,
    action: 'PRICE_OVERRIDE',
    entityType: 'so_line',
    entityId: `${input.soNumber}:${input.sku}`,
    summary: summarizeSoLineOverride(input),
    reason: input.reason,
    actorName,
    createdAt: new Date().toISOString(),
  };
  usePricingStore.getState().appendAuditRow(row);
}

// ---------------------------------------------------------------------------
// S3.3 — explicit re-resolve DETACH row (DA-6; §6.2 detach-consequence)
// ---------------------------------------------------------------------------

export interface SoLineReResolveAuditInput {
  /**
   * Always a real soNumber (RULING-7): modify writes immediately; create
   * queues on the draft line and flushes at successful save.
   */
  soRef: string;
  sku: string;
  currency: string;
  /** The line qty the explicit action re-resolved at. */
  qty: number;
  /** The manual override the action replaced. */
  overriddenUnitPrice: number;
  /** The recorded business reason the detach discards. */
  overrideReason: string;
  /** Fresh system-resolved price; null = unresolvable. */
  resolvedUnitPrice: number | null;
}

/** "manual 175.00 → re-resolved 140.00 AUD @ qty 50" one-liner (store style). */
export function summarizeSoLineReResolve(input: SoLineReResolveAuditInput): string {
  const resolved =
    input.resolvedUnitPrice !== null ? input.resolvedUnitPrice.toFixed(2) : 'unresolved';
  return `${input.soRef} · ${input.sku}: manual ${input.overriddenUnitPrice.toFixed(2)} → re-resolved ${resolved} ${input.currency} @ qty ${input.qty}`;
}

/**
 * Append the detach row when an explicit per-line re-resolve replaces a
 * manual override — only the user-triggered S3.3 seat may call this (the
 * automatic effect paths never detach silently; BF4 stays refused). Per
 * RULING-7 the create flow calls this from the post-save loop (queued
 * snapshot + real soNumber), never at click time.
 */
export function appendSoLineReResolveAudit(input: SoLineReResolveAuditInput): void {
  const actorName = (getUserProfile() as User | null)?.name ?? 'unknown';
  const row: PriceOverrideAuditRow = {
    id: `so-reresolve-${Date.now()}-${(auditSeq += 1)}`,
    action: 'PRICE_OVERRIDE',
    entityType: 'so_line',
    entityId: `${input.soRef}:${input.sku}`,
    summary: summarizeSoLineReResolve(input),
    reason: `explicit re-resolve at qty ${input.qty} — detached override reason: ${input.overrideReason}`,
    actorName,
    createdAt: new Date().toISOString(),
  };
  usePricingStore.getState().appendAuditRow(row);
}
